import { useCallback, useContext } from "react";

import { useTranslation } from "next-i18next";

import { useAuth } from "@/auth";
import {
  ProductVat,
  fetchCartVat,
  fetchMerchantInvoiceSettings,
  fetchOrderInvoiceFields,
  fetchProductVat,
  fetchViewerIsBusiness,
} from "@/features/seller-invoice/data/seller-invoice-client";
import {
  SELLER_INVOICE_COPY,
  SELLER_INVOICE_UI_GATE,
  SellerInvoiceCopyKey,
} from "@/features/seller-invoice/static/seller-invoice.static";
import { toSellerInvoiceLocale } from "@/features/seller-invoice/utils/seller-invoice.utils";
import { useFeatureGate } from "@/features/statsig/gate.hooks";
import {
  QueryClient,
  QueryClientContext,
  UseQueryOptions,
  useQuery as useTanstackQuery,
} from "@tanstack/react-query";

/*
 * Every new surface sits in widely rendered components (cards, cart, account
 * pages). Where no QueryClientProvider is mounted (unit tests of those
 * components), the queries run against an inert client and stay disabled.
 */
const INERT_CLIENT = new QueryClient();

const useQuery = <TData>(options: UseQueryOptions<TData>) => {
  const client = useContext(QueryClientContext);

  return useTanstackQuery(
    client ? options : { ...options, enabled: false },
    client ?? INERT_CLIENT
  );
};

/** Viewer-scoped: every key carries the viewer id. */
export const SELLER_INVOICE_QUERY_KEY = ["seller-invoice"] as const;

/** `t()` over the WHO-6309 copy table, with the viewer's locale as the interim fallback. */
export const useSellerInvoiceCopy = () => {
  const { t, i18n } = useTranslation();
  const locale = toSellerInvoiceLocale(i18n?.language);

  const copy = useCallback(
    (key: SellerInvoiceCopyKey, values?: Record<string, string | number>) =>
      t(key, { defaultValue: SELLER_INVOICE_COPY[key][locale], ...values }),
    [locale, t]
  );

  return { copy, t, locale };
};

/**
 * The single gate for every surface: Statsig `seller_invoice_ui` on and a
 * signed-in viewer (client-side only, so SSR HTML never changes).
 */
export const useSellerInvoiceViewer = () => {
  const { isEnabled } = useFeatureGate(SELLER_INVOICE_UI_GATE);
  const { token, isLoggedIn, isReady, userId } = useAuth();
  const enabled = isEnabled && isReady && isLoggedIn && !!token;

  const business = useQuery({
    queryKey: [...SELLER_INVOICE_QUERY_KEY, "viewer-business", userId],
    queryFn: () => fetchViewerIsBusiness(token),
    enabled,
    staleTime: 5 * 60 * 1000,
  });

  return {
    enabled,
    isBusinessViewer: enabled && business.data === true,
    token: enabled ? token : null,
    userId,
  };
};

/* Search pages render 24 cards; ids asked in the same tick go out as one request. */
type Waiter = (vat: ProductVat | null) => void;
const pending = new Map<string, Map<string, Waiter[]>>();
let scheduled = false;

const flush = () => {
  scheduled = false;
  const batches = Array.from(pending.entries());
  pending.clear();

  for (const [token, waitersById] of batches) {
    const ids = Array.from(waitersById.keys());

    for (let start = 0; start < ids.length; start += 24) {
      const chunk = ids.slice(start, start + 24);

      void fetchProductVat(token, chunk).then(vats => {
        for (const id of chunk) {
          waitersById
            .get(id)
            ?.forEach(resolve => resolve(vats.get(id) ?? null));
        }
      });
    }
  }
};

const loadProductVat = (token: string, productId: string) =>
  new Promise<ProductVat | null>(resolve => {
    const waitersById = pending.get(token) ?? new Map<string, Waiter[]>();
    waitersById.set(productId, [
      ...(waitersById.get(productId) ?? []),
      resolve,
    ]);
    pending.set(token, waitersById);

    if (!scheduled) {
      scheduled = true;
      setTimeout(flush, 0);
    }
  });

/** `Product.vatRegime` + `priceExclVat`, only asked for a business viewer. */
export const useProductVat = (productId: string | null | undefined) => {
  const viewer = useSellerInvoiceViewer();
  const enabled = viewer.isBusinessViewer && !!viewer.token && !!productId;

  const query = useQuery({
    queryKey: [
      ...SELLER_INVOICE_QUERY_KEY,
      "product",
      viewer.userId,
      productId,
    ],
    queryFn: () => loadProductVat(viewer.token ?? "", productId ?? ""),
    enabled,
  });

  return {
    isBusinessViewer: viewer.isBusinessViewer,
    vat: enabled ? query.data ?? null : null,
  };
};

/** The cart item VAT fields, re-read when the cart changes (`revision`). */
export const useCartVat = (revision: string) => {
  const viewer = useSellerInvoiceViewer();
  const enabled = viewer.isBusinessViewer && !!viewer.token;

  const query = useQuery({
    queryKey: [...SELLER_INVOICE_QUERY_KEY, "cart", viewer.userId, revision],
    queryFn: () => fetchCartVat(viewer.token),
    enabled,
  });

  return {
    isBusinessViewer: viewer.isBusinessViewer,
    lines: enabled ? query.data ?? null : null,
  };
};

export const useOrderInvoiceFields = (orderId: string | null | undefined) => {
  const viewer = useSellerInvoiceViewer();
  const enabled = viewer.enabled && !!orderId;

  const query = useQuery({
    queryKey: [...SELLER_INVOICE_QUERY_KEY, "order", viewer.userId, orderId],
    queryFn: () => fetchOrderInvoiceFields(viewer.token, orderId ?? ""),
    enabled,
  });

  return {
    enabled,
    token: viewer.token,
    fields: enabled ? query.data ?? null : null,
    refetch: query.refetch,
  };
};

export const useMerchantInvoiceSettings = () => {
  const viewer = useSellerInvoiceViewer();
  const enabled = viewer.isBusinessViewer && !!viewer.token;

  const query = useQuery({
    queryKey: [...SELLER_INVOICE_QUERY_KEY, "merchant", viewer.userId],
    queryFn: () => fetchMerchantInvoiceSettings(viewer.token),
    enabled,
  });

  return {
    enabled,
    token: viewer.token,
    settings: enabled ? query.data ?? null : null,
    refetch: query.refetch,
  };
};
